import React, { useState, useEffect, useCallback } from 'react';
import { expensesApi, staffApi } from '../api/client';
import { Badge, Modal, Spin, Empty, useFlash, F, Confirm, parseErr } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import { Wallet, Trash, Edit, Car } from '../components/Icons';

const CATEGORIES = ['Meals', 'Fuel', 'Snacks', 'Stationary Items', 'Vendor', 'Daily Allowance', 'Others'];
const VENDOR_WORK_TYPES = ['Solar Installation', 'Welding', 'Electrician', 'Plumbing', 'Carpentry', 'Others'];

const VEHICLE_DATA = {
  Car: {
    'Maruti Omni':     'KL-31-J-9779',
    'Maruti Eeco':     'KL-31-H-2223',
    'Mahindra Bolero': 'KL-31-S-2223',
    'Ford Ecosport':   'KL-27-G-8668',
  },
  Scooter: ['KL-31-P-2223', 'KL-31-N-1420'],
};
const CAR_MODELS   = Object.keys(VEHICLE_DATA.Car);
const FIRST_CAR    = CAR_MODELS[0];
const FIRST_SCOOTER = VEHICLE_DATA.Scooter[0];

const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

const emptyForm = () => ({
  category:             'Meals',
  description:          '',
  vehicle_type:         'Car',
  vehicle_model:        FIRST_CAR,
  reg_number:           VEHICLE_DATA.Car[FIRST_CAR],
  amount:               '',
  expense_date:         todayStr(),
  notes:                '',
  meal_staff:           [],
  meal_others:          false,
  vendor_name:          '',
  vendor_work:          'Solar Installation',
  allowance_mode:       'individual',
  allowance_rows:       [{ id: 1, staff_id: '', amount: '' }],
  allowance_bulk_staff: [],
  allowance_bulk_amount:'',
});

function buildTitle(form, staffList = []) {
  if (form.category === 'Fuel') {
    if (form.vehicle_type === 'Scooter') return `Fuel - Scooter (${form.reg_number})`;
    const reg = (VEHICLE_DATA.Car[form.vehicle_model] || form.reg_number || '').trim();
    return `Fuel - ${form.vehicle_model}${reg ? ` (${reg})` : ''}`;
  }
  if (form.category === 'Meals') {
    const names = staffList.filter(s => (form.meal_staff || []).includes(s.id)).map(s => s.name);
    if (form.meal_others) names.push('Others');
    return names.length > 0 ? `Meals for ${names.join(', ')}` : 'Meals';
  }
  if (form.category === 'Vendor') {
    const name = form.vendor_name.trim() || 'Unknown Vendor';
    return `Vendor: ${name} (${form.vendor_work})`;
  }
  if (form.category === 'Snacks') return 'Snacks';
  return form.description.trim() || form.category;
}

function parseFuelTitle(title) {
  const match = title.match(/^Fuel - (.+?)(?:\s*\(([^)]+)\))?$/);
  const vehicleStr = match?.[1]?.trim() || '';
  const regStr     = match?.[2]?.trim() || '';
  if (vehicleStr === 'Scooter') {
    return { vehicle_type: 'Scooter', vehicle_model: '', reg_number: VEHICLE_DATA.Scooter.includes(regStr) ? regStr : FIRST_SCOOTER };
  }
  const model = CAR_MODELS.includes(vehicleStr) ? vehicleStr : FIRST_CAR;
  return { vehicle_type: 'Car', vehicle_model: model, reg_number: regStr || VEHICLE_DATA.Car[model] || '' };
}

export default function Expenses() {
  const { canDelete, isAdmin } = useAuth();
  const [rows, setRows]       = useState([]);
  const [staffList, setStaff] = useState([]);
  const [busy, setBusy]       = useState(true);
  const [showAdd, setAdd]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [del, setDel]         = useState(null);
  const [form, setForm]       = useState(emptyForm());
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');
  const { show, Flash }       = useFlash();
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [{ data: expenses }, { data: staff }] = await Promise.all([
        expensesApi.list({ page_size: 500 }),
        staffApi.list(),
      ]);
      setRows(expenses);
      setStaff(staff.filter(s => s.is_active && s.role !== 'superadmin'));
    }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(emptyForm()); setErr(''); setEditing(null); setAdd(true); };

  const openEdit = e => {
    setEditing(e);
    const base = { amount: String(e.amount), expense_date: e.expense_date, notes: e.notes || '' };
    if (e.category === 'Fuel') {
      setForm({ ...emptyForm(), category: 'Fuel', description: '', ...parseFuelTitle(e.title), ...base });
    } else if (e.category === 'Meals') {
      const m = e.title.match(/^Meals for (.+)$/);
      const names = m ? m[1].split(', ') : [];
      const hasOthers = names.includes('Others');
      const staffNames = names.filter(n => n !== 'Others');
      const meal_staff = staffList.filter(s => staffNames.includes(s.name)).map(s => s.id);
      setForm({ ...emptyForm(), category: 'Meals', meal_staff, meal_others: hasOthers, ...base });
    } else if (e.category === 'Vendor') {
      const m = e.title.match(/^Vendor: (.+?) \((.+)\)$/);
      setForm({ ...emptyForm(), category: 'Vendor', vendor_name: m?.[1] || '', vendor_work: m?.[2] || 'Solar Installation', ...base });
    } else if (e.category === 'Daily Allowance') {
      setForm({ ...emptyForm(), category: 'Daily Allowance', description: e.title, ...base });
    } else {
      setForm({ ...emptyForm(), category: e.category, description: e.title, ...base });
    }
    setErr(''); setAdd(true);
  };

  const onVehicleType = e => {
    const type = e.target.value;
    if (type === 'Car') {
      setForm(f => ({ ...f, vehicle_type: 'Car', vehicle_model: FIRST_CAR, reg_number: VEHICLE_DATA.Car[FIRST_CAR] }));
    } else {
      setForm(f => ({ ...f, vehicle_type: 'Scooter', vehicle_model: '', reg_number: FIRST_SCOOTER }));
    }
  };

  const onCarModel = e => {
    const model = e.target.value;
    setForm(f => ({ ...f, vehicle_model: model, reg_number: VEHICLE_DATA.Car[model] || '' }));
  };

  const save = async () => {
    // ── Daily Allowance (editing = single record) ────────────────────────────
    if (form.category === 'Daily Allowance' && editing) {
      if (!form.amount || isNaN(parseFloat(form.amount))) { setErr('Enter a valid amount.'); return; }
      setSaving(true); setErr('');
      try {
        const { data } = await expensesApi.update(editing.id, {
          title: editing.title, category: 'Daily Allowance',
          amount: parseFloat(form.amount), expense_date: form.expense_date, notes: form.notes || undefined,
        });
        setRows(r => r.map(x => x.id === data.id ? data : x));
        show('Allowance updated!'); setAdd(false);
      } catch(e) { setErr(parseErr(e, 'Failed to update.')); }
      finally { setSaving(false); }
      return;
    }

    // ── Daily Allowance (new = multiple records) ─────────────────────────────
    if (form.category === 'Daily Allowance') {
      let records = [];
      if (form.allowance_mode === 'individual') {
        const valid = form.allowance_rows.filter(r => r.staff_id && r.amount && parseFloat(r.amount) > 0);
        if (!valid.length) { setErr('Add at least one staff row with a valid amount.'); return; }
        const incomplete = form.allowance_rows.some(r => (r.staff_id && (!r.amount || parseFloat(r.amount) <= 0)) || (!r.staff_id && r.amount));
        if (incomplete) { setErr('Each row needs both a staff member and an amount.'); return; }
        records = valid.map(r => ({
          title: `Daily Allowance — ${staffList.find(s => s.id === r.staff_id)?.name || 'Staff'}`,
          category: 'Daily Allowance', amount: parseFloat(r.amount),
          expense_date: form.expense_date, notes: form.notes || undefined,
        }));
      } else {
        if (!form.allowance_bulk_staff.length) { setErr('Select at least one staff member.'); return; }
        if (!form.allowance_bulk_amount || parseFloat(form.allowance_bulk_amount) <= 0) { setErr('Enter a valid amount per staff.'); return; }
        records = form.allowance_bulk_staff.map(id => ({
          title: `Daily Allowance — ${staffList.find(s => s.id === id)?.name || 'Staff'}`,
          category: 'Daily Allowance', amount: parseFloat(form.allowance_bulk_amount),
          expense_date: form.expense_date, notes: form.notes || undefined,
        }));
      }
      setSaving(true); setErr('');
      try {
        const results = await Promise.all(records.map(p => expensesApi.create(p)));
        setRows(r => [...results.map(x => x.data), ...r]);
        show(`${records.length} allowance record${records.length > 1 ? 's' : ''} added!`);
        setAdd(false);
      } catch(e) { setErr(parseErr(e, 'Failed to save allowances.')); }
      finally { setSaving(false); }
      return;
    }

    // ── All other categories ─────────────────────────────────────────────────
    if (!form.amount || isNaN(parseFloat(form.amount))) { setErr('Enter a valid amount.'); return; }
    if (form.category === 'Meals' && !form.meal_staff.length && !form.meal_others) { setErr('Select at least one person for Meals.'); return; }
    if (form.category === 'Vendor' && !form.vendor_name.trim()) { setErr('Vendor name is required.'); return; }
    if (!['Fuel', 'Meals', 'Vendor', 'Snacks'].includes(form.category) && !form.description.trim()) { setErr('Description is required.'); return; }
    setSaving(true); setErr('');
    const payload = {
      title:        buildTitle(form, staffList),
      category:     form.category,
      amount:       parseFloat(form.amount),
      expense_date: form.expense_date,
      notes:        form.notes || undefined,
    };
    try {
      if (editing) {
        const { data } = await expensesApi.update(editing.id, payload);
        setRows(r => r.map(x => x.id === data.id ? data : x));
        show('Expense updated!');
      } else {
        const { data } = await expensesApi.create(payload);
        setRows(r => [data, ...r]);
        show('Expense added!');
      }
      setAdd(false);
    } catch(e) { setErr(parseErr(e, 'Failed to save expense.')); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await expensesApi.delete(del.id); setRows(r => r.filter(x => x.id !== del.id)); show('Deleted.'); }
    catch { show('Delete failed.', 'error'); } finally { setDel(null); }
  };

  const total = rows.reduce((s, e) => s + Number(e.amount), 0);
  const fmt   = n => '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });

  if (busy) return <Spin/>;
  return (
    <div>
      <Flash/>
      <div className="page-hd">
        <h2>Expenses</h2>
        {isAdmin && <button className="btn btn-primary" onClick={openAdd}>+ Add Expense</button>}
      </div>

      <div className="stat-grid" style={{ gridTemplateColumns:'repeat(3,1fr)' }}>
        <div className="stat-card">
          <div className="stat-lbl">Total Expenses</div>
          <div className="stat-val" style={{ color:'var(--red)' }}>{fmt(total)}</div>
          <div className="stat-sub">{rows.length} entr{rows.length !== 1 ? 'ies' : 'y'}</div>
        </div>
        {CATEGORIES.map(cat => {
          const catRows  = rows.filter(r => r.category === cat);
          const catTotal = catRows.reduce((s, r) => s + Number(r.amount), 0);
          return catTotal > 0 ? (
            <div key={cat} className="stat-card">
              <div className="stat-lbl">{cat}</div>
              <div className="stat-val" style={{ fontSize:20 }}>{fmt(catTotal)}</div>
              <div className="stat-sub">{catRows.length} entr{catRows.length !== 1 ? 'ies' : 'y'}</div>
            </div>
          ) : null;
        })}
      </div>

      <div className="card card-0">
        {rows.length === 0 ? <Empty msg="No expenses recorded yet"/> : (
          <div className="tbl-wrap"><table>
            <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Amount</th><th>Notes</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.map(e => (
                <tr key={e.id}>
                  <td style={{ fontSize:12 }}>{e.expense_date}</td>
                  <td>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}>
                      {e.category === 'Fuel' && <Car s={13} style={{ color:'var(--amber)' }}/>}
                      {e.category}
                    </span>
                  </td>
                  <td style={{ fontWeight:500 }}>{e.title}</td>
                  <td style={{ fontWeight:600, color:'var(--red)' }}>{fmt(e.amount)}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{e.notes || '—'}</td>
                  <td style={{ display:'flex', gap:4 }}>
                    {isAdmin && <button className="btn btn-sm" onClick={() => openEdit(e)} title="Edit"><Edit s={13}/></button>}
                    {canDelete && <button className="btn btn-sm btn-danger" onClick={() => setDel(e)} title="Delete"><Trash s={13}/></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <Modal open={showAdd} onClose={() => setAdd(false)} title={editing ? 'Edit Expense' : 'Add Expense'}
        footer={<><button className="btn" onClick={() => setAdd(false)}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : editing ? 'Update' : 'Add Expense'}</button></>}
      >
        {err && <div className="notice n-err" style={{ marginBottom:10 }}>{err}</div>}
        <div className="fg-2">
          <F label="Category">
            <select value={form.category} onChange={set('category')}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </F>
          <F label="Date">
            <input type="date" value={form.expense_date} onChange={set('expense_date')} max={todayStr()}/>
          </F>

          {form.category === 'Fuel' && (<>
            <F label="Vehicle Type">
              <select value={form.vehicle_type} onChange={onVehicleType}>
                <option>Car</option>
                <option>Scooter</option>
              </select>
            </F>
            {form.vehicle_type === 'Car' ? (<>
              <F label="Car Model">
                <select value={form.vehicle_model} onChange={onCarModel}>
                  {CAR_MODELS.map(m => <option key={m}>{m}</option>)}
                </select>
              </F>
              <F label="Registration No.">
                {VEHICLE_DATA.Car[form.vehicle_model]
                  ? <input value={VEHICLE_DATA.Car[form.vehicle_model]} readOnly style={{ background:'var(--surface)', color:'var(--text2)', cursor:'default' }}/>
                  : <input value={form.reg_number} onChange={set('reg_number')} placeholder="e.g. KL-XX-XX-XXXX"/>
                }
              </F>
            </>) : (
              <F label="Registration No." full>
                <select value={form.reg_number} onChange={set('reg_number')}>
                  {VEHICLE_DATA.Scooter.map(r => <option key={r}>{r}</option>)}
                </select>
              </F>
            )}
          </>)}

          {form.category === 'Meals' && (
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>
                Paid for <span style={{ color: 'var(--red)' }}>*</span>
              </div>
              <select
                value=""
                onChange={e => {
                  const val = e.target.value;
                  if (!val) return;
                  if (val === '__others__') {
                    setForm(f => ({ ...f, meal_others: true }));
                  } else {
                    setForm(f => ({
                      ...f,
                      meal_staff: (f.meal_staff || []).includes(val)
                        ? f.meal_staff
                        : [...(f.meal_staff || []), val],
                    }));
                  }
                }}
                style={{ width: '100%', marginBottom: 8 }}
              >
                <option value="">— Select person —</option>
                {staffList
                  .filter(s => !(form.meal_staff || []).includes(s.id))
                  .map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)
                }
                {!form.meal_others && <option value="__others__">Others (non-employee)</option>}
              </select>
              {((form.meal_staff || []).length > 0 || form.meal_others) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(form.meal_staff || []).map(id => {
                    const s = staffList.find(x => x.id === id);
                    return s ? (
                      <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 500 }}>
                        {s.name}
                        <button type="button" onClick={() => setForm(f => ({ ...f, meal_staff: f.meal_staff.filter(x => x !== id) }))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                      </span>
                    ) : null;
                  })}
                  {form.meal_others && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 500 }}>
                      Others
                      <button type="button" onClick={() => setForm(f => ({ ...f, meal_others: false }))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {form.category === 'Vendor' && (<>
            <F label="Vendor Name" req full>
              <input value={form.vendor_name} onChange={set('vendor_name')} placeholder="e.g. Rajan Electricals"/>
            </F>
            <F label="Work Type" req full>
              <select value={form.vendor_work} onChange={set('vendor_work')}>
                {VENDOR_WORK_TYPES.map(w => <option key={w}>{w}</option>)}
              </select>
            </F>
          </>)}

          {!['Fuel', 'Meals', 'Vendor', 'Snacks', 'Daily Allowance'].includes(form.category) && (
            <F label="Description" req full>
              <input value={form.description} onChange={set('description')} placeholder={`e.g. ${form.category} expense`}/>
            </F>
          )}

          {/* Daily Allowance — editing a single record */}
          {form.category === 'Daily Allowance' && editing && (
            <div style={{ gridColumn:'1 / -1' }}>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:4 }}>Staff</div>
              <div style={{ padding:'9px 12px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, fontSize:14, marginBottom:12, color:'var(--text)' }}>
                {form.description}
              </div>
            </div>
          )}

          {/* Daily Allowance — new record(s) */}
          {form.category === 'Daily Allowance' && !editing && (
            <div style={{ gridColumn:'1 / -1' }}>
              {/* Mode toggle */}
              <div style={{ display:'flex', gap:8, marginBottom:16 }}>
                {[['individual','Different amounts per staff'],['bulk','Same amount for all']].map(([mode, label]) => (
                  <button key={mode} type="button"
                    onClick={() => setForm(f => ({ ...f, allowance_mode: mode }))}
                    style={{
                      flex:1, padding:'9px 8px', fontSize:13, fontWeight:600, borderRadius:8, cursor:'pointer',
                      border:`2px solid ${form.allowance_mode === mode ? 'var(--navy)' : 'var(--border)'}`,
                      background: form.allowance_mode === mode ? 'var(--navy)' : '#fff',
                      color: form.allowance_mode === mode ? '#fff' : 'var(--text2)',
                    }}
                  >{label}</button>
                ))}
              </div>

              {form.allowance_mode === 'individual' ? (
                <div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 130px 34px', gap:6, marginBottom:4, padding:'0 2px' }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'var(--text2)' }}>STAFF MEMBER</div>
                    <div style={{ fontSize:11, fontWeight:700, color:'var(--text2)' }}>AMOUNT (₹)</div>
                    <div/>
                  </div>
                  {form.allowance_rows.map((row, idx) => (
                    <div key={row.id} style={{ display:'grid', gridTemplateColumns:'1fr 130px 34px', gap:6, marginBottom:8, alignItems:'center' }}>
                      <select value={row.staff_id}
                        onChange={e => setForm(f => ({ ...f, allowance_rows: f.allowance_rows.map((r, i) => i === idx ? { ...r, staff_id: e.target.value } : r) }))}
                      >
                        <option value="">— Select staff —</option>
                        {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                      <input type="number" min="0" step="0.01" placeholder="0.00"
                        value={row.amount}
                        onChange={e => setForm(f => ({ ...f, allowance_rows: f.allowance_rows.map((r, i) => i === idx ? { ...r, amount: e.target.value } : r) }))}
                      />
                      <button type="button"
                        disabled={form.allowance_rows.length === 1}
                        onClick={() => setForm(f => ({ ...f, allowance_rows: f.allowance_rows.filter((_, i) => i !== idx) }))}
                        style={{ width:34, height:34, background:'none', border:'1px solid var(--border)', borderRadius:6, cursor: form.allowance_rows.length === 1 ? 'not-allowed' : 'pointer', color:'var(--red)', opacity: form.allowance_rows.length === 1 ? 0.3 : 1, fontSize:18, padding:0 }}
                      >×</button>
                    </div>
                  ))}
                  <button type="button"
                    onClick={() => setForm(f => ({ ...f, allowance_rows: [...f.allowance_rows, { id: Date.now(), staff_id: '', amount: '' }] }))}
                    style={{ marginTop:4, fontSize:13, fontWeight:600, color:'var(--navy)', background:'none', border:'1.5px dashed var(--navy)', borderRadius:8, padding:'7px 16px', cursor:'pointer' }}
                  >+ Add Row</button>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:6 }}>
                    Select Staff <span style={{ color:'var(--red)' }}>*</span>
                  </div>
                  <select value=""
                    onChange={e => {
                      const val = e.target.value; if (!val) return;
                      setForm(f => ({ ...f, allowance_bulk_staff: f.allowance_bulk_staff.includes(val) ? f.allowance_bulk_staff : [...f.allowance_bulk_staff, val] }));
                    }}
                    style={{ width:'100%', marginBottom:8 }}
                  >
                    <option value="">— Add staff member —</option>
                    {staffList.filter(s => !form.allowance_bulk_staff.includes(s.id)).map(s => (
                      <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
                    ))}
                  </select>
                  {form.allowance_bulk_staff.length > 0 && (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
                      {form.allowance_bulk_staff.map(id => {
                        const s = staffList.find(x => x.id === id);
                        return s ? (
                          <span key={id} style={{ display:'inline-flex', alignItems:'center', gap:5, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:500 }}>
                            {s.name}
                            <button type="button" onClick={() => setForm(f => ({ ...f, allowance_bulk_staff: f.allowance_bulk_staff.filter(x => x !== id) }))}
                              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text2)', fontSize:14, lineHeight:1, padding:0 }}>×</button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                  <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:6 }}>
                    Amount per Staff (₹) <span style={{ color:'var(--red)' }}>*</span>
                  </div>
                  <input type="number" min="0" step="0.01" placeholder="0.00"
                    value={form.allowance_bulk_amount}
                    onChange={e => setForm(f => ({ ...f, allowance_bulk_amount: e.target.value }))}
                    style={{ width:'100%', boxSizing:'border-box' }}
                  />
                </div>
              )}
            </div>
          )}

          {(form.category !== 'Daily Allowance' || !!editing) && (
            <F label="Amount (₹)" req>
              <input type="number" min="0" step="0.01" value={form.amount} onChange={set('amount')} placeholder="0.00"/>
            </F>
          )}
          <F label="Notes" full>
            <textarea value={form.notes} onChange={set('notes')} placeholder="Optional notes…" style={{ minHeight:60 }}/>
          </F>
        </div>
      </Modal>

      <Confirm open={!!del} onClose={() => setDel(null)} onOk={doDelete}
        msg={`Delete expense "${del?.title}" of ${del ? fmt(del.amount) : ''}?`}/>
    </div>
  );
}
